import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const cache = {
    imageBlob: null,
    imageDataUrl: null,
    pdfFile: null,
    fileNameBase: null
};

let isGenerating = false;
let generationPromise = null;

function pad(value) {
    return String(value).padStart(2, '0');
}

function buildTimestamp() {
    const now = new Date();
    const year = now.getFullYear();
    const month = pad(now.getMonth() + 1);
    const day = pad(now.getDate());
    const hours = pad(now.getHours());
    const minutes = pad(now.getMinutes());
    const seconds = pad(now.getSeconds());

    return `${year}${month}${day}-${hours}${minutes}${seconds}`;
}

function buildFileNameBase(orderId) {
    const orderSuffix = orderId ? `-${orderId}` : '';
    return `comprovante${orderSuffix}-${buildTimestamp()}`;
}

function dataUrlToBlob(dataUrl) {
    const [meta, base64Data] = dataUrl.split(',');
    const mimeMatch = /:(.*?);/.exec(meta);
    const mimeType = mimeMatch ? mimeMatch[1] : 'image/png';
    const binary = atob(base64Data);
    const length = binary.length;
    const bytes = new Uint8Array(length);

    for (let index = 0; index < length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
    }

    return new Blob([bytes], { type: mimeType });
}

function canvasToBlob(canvas, type = 'image/png', quality = 1) {
    return new Promise(resolve => {
        if (canvas.toBlob) {
            canvas.toBlob(blob => {
                if (blob) {
                    resolve(blob);
                    return;
                }

                resolve(dataUrlToBlob(canvas.toDataURL(type, quality)));
            }, type, quality);
            return;
        }

        resolve(dataUrlToBlob(canvas.toDataURL(type, quality)));
    });
}

function downloadBlob(blob, fileName) {
    const fileUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = fileUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(fileUrl);
}

function createShareFile(blob, fileName) {
    if (typeof File === 'function') {
        return new File([blob], fileName, {
            type: 'application/pdf'
        });
    }

    blob.name = fileName;
    return blob;
}

async function waitForFonts() {
    if (document.fonts && document.fonts.ready) {
        try {
            await document.fonts.ready;
        } catch (error) {
            // segue a captura mesmo sem confirmação de fonte
        }
    }
}

function getCaptureOptions(target) {
    const width = Math.max(target.scrollWidth, document.documentElement.clientWidth, window.innerWidth || 0);
    const height = Math.max(target.scrollHeight, document.documentElement.clientHeight, window.innerHeight || 0);

    return {
        useCORS: true,
        backgroundColor: '#FFFFFF',
        scale: Math.min(window.devicePixelRatio || 1, 2),
        width,
        height,
        windowWidth: width,
        windowHeight: height,
        scrollX: 0,
        scrollY: 0
    };
}

function getPdfBlobFromImage(imageDataUrl) {
    const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const maxWidth = pageWidth - margin * 2;
    const maxHeight = pageHeight - margin * 2;
    const imageProps = pdf.getImageProperties(imageDataUrl);
    const imageRatio = imageProps.width / imageProps.height;
    let renderWidth = maxWidth;
    let renderHeight = renderWidth / imageRatio;

    if (renderHeight > maxHeight) {
        renderHeight = maxHeight;
        renderWidth = renderHeight * imageRatio;
    }

    const x = (pageWidth - renderWidth) / 2;
    const y = (pageHeight - renderHeight) / 2;

    pdf.addImage(imageDataUrl, 'PNG', x, y, renderWidth, renderHeight);

    return pdf.output('blob');
}

async function captureScreenOnce(options = {}) {
    if (cache.imageBlob && cache.imageDataUrl) {
        if (!cache.fileNameBase) {
            cache.fileNameBase = buildFileNameBase(options.orderId);
        }

        return cache;
    }

    if (generationPromise) {
        return generationPromise;
    }

    generationPromise = (async () => {
        isGenerating = true;

        await waitForFonts();

        const target = document.documentElement;
        const captureOptions = getCaptureOptions(target);
        const canvas = await html2canvas(target, captureOptions);
        const imageBlob = await canvasToBlob(canvas, 'image/png', 1);
        const imageDataUrl = canvas.toDataURL('image/png', 1);

        cache.imageBlob = imageBlob;
        cache.imageDataUrl = imageDataUrl;
        cache.fileNameBase = buildFileNameBase(options.orderId);

        return cache;
    })().finally(() => {
        isGenerating = false;
        generationPromise = null;
    });

    return generationPromise;
}

async function ensurePdf(options = {}) {
    const capture = await captureScreenOnce(options);

    if (capture.pdfFile) {
        return capture;
    }

    const pdfBlob = getPdfBlobFromImage(capture.imageDataUrl);
    capture.pdfFile = createShareFile(pdfBlob, `${capture.fileNameBase}.pdf`);

    return capture;
}

function canShareFile(file) {
    if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
        return false;
    }

    if (typeof navigator.canShare !== 'function') {
        return true;
    }

    try {
        return navigator.canShare({ files: [file] });
    } catch (error) {
        return false;
    }
}

export default {
    get isGenerating() {
        return isGenerating;
    },

    async captureScreenOnce(options = {}) {
        return captureScreenOnce(options);
    },

    async downloadPng(options = {}) {
        const capture = await captureScreenOnce(options);
        downloadBlob(capture.imageBlob, `${capture.fileNameBase}.png`);
    },

    async sharePdf(options = {}) {
        const capture = await ensurePdf(options);
        const sharedFile = capture.pdfFile;

        if (canShareFile(sharedFile)) {
            try {
                await navigator.share({
                    title: options.title || 'Comprovante de pagamento',
                    files: [sharedFile]
                });
                return;
            } catch (error) {
                if (error && error.name === 'AbortError') {
                    return;
                }
            }
        }

        downloadBlob(sharedFile, `${capture.fileNameBase}.pdf`);
    },
    
    clearCache() {
        cache.imageBlob = null;
        cache.imageDataUrl = null;
        cache.pdfFile = null;
        cache.fileNameBase = null;
    }
};
